import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FilterSectionKey } from './DetailFilterBar'
import { getSections } from './constants'
import SuggestionTextSearch from './SuggestionTextSearch'
import { IconCheck } from './Icon'
import { urlParamsUtils, useModalScrollLock } from './utils'

interface DetailFilterModalProps {
  open: boolean
  onClose: () => void
  scrollToSectionKey?: FilterSectionKey
}

// TagButton コンポーネント
interface TagButtonProps {
  label: string
  checked: boolean
  onClick: () => void
  className?: string
}

const TagButton: React.FC<TagButtonProps> = ({ label, checked, onClick, className = '' }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center px-5 py-2 rounded-full border text-sm font-noto transition min-w-[120px] justify-center ${
        checked ? 'border-green text-green bg-white' : 'border-gray-200 text-black bg-white'
      } ${className}`}
      style={{ fontSize: 12, lineHeight: '18px', fontWeight: 'normal', borderWidth: 1 }}
    >
      {checked && <IconCheck className="mr-1" />}
      {label}
    </button>
  )
}

const isMobileScreen = window.innerWidth < 768
const isTabletScreen = window.innerWidth < 1024 && window.innerWidth > 780
const isSmallMobileScreen = window.innerWidth <= 360

export const DetailFilterModal: React.FC<DetailFilterModalProps> = ({ open, onClose, scrollToSectionKey }) => {
  const { t } = useTranslation()
  const sections = React.useMemo(() => getSections(t), [t])
  const sectionsRef = useRef(sections)
  sectionsRef.current = sections

  const sectionRefs = useRef<Record<FilterSectionKey, HTMLDivElement | null>>({
    popular: null,
    experience: null,
    facility: null,
    accommodation: null,
    budget: null,
    life: null,
    privilege: null,
    transport: null,
    child: null,
    area: null,
    text_search: null,
  })
  const [selectedPopularTags, setSelectedPopularTags] = useState<string[]>([])
  const [selectedExperienceTags, setSelectedExperienceTags] = useState<string[]>([])
  const [selectedFacilityTags, setSelectedFacilityTags] = useState<string[]>([])
  const [selectedAccommodationTags, setSelectedAccommodationTags] = useState<string[]>([])
  const [selectedBudgetTags, setSelectedBudgetTags] = useState<string[]>([])
  const [selectedLifeTags, setSelectedLifeTags] = useState<string[]>([])
  const [selectedPrivilegeTags, setSelectedPrivilegeTags] = useState<string[]>([])
  const [selectedTransportTags, setSelectedTransportTags] = useState<string[]>([])
  const [selectedChildTags, setSelectedChildTags] = useState<string[]>([])
  const [selectedAreaTags, setSelectedAreaTags] = useState<string[]>([])
  const [searchText, setSearchText] = useState('')

  const handleSearchChange = (key: string, value: string, _type?: 'down' | 'up' | 'option' | 'text') => {
    if (key === 'text_search') {
      setSearchText(value)
    }
  }

  // Prevent background scroll when modal is open
  useModalScrollLock(open)

  useEffect(() => {
    if (open && scrollToSectionKey && sectionRefs.current[scrollToSectionKey]) {
      sectionRefs.current[scrollToSectionKey]?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [open, scrollToSectionKey])

  // 初期表示時にURLから各タグを取得して反映
  useEffect(() => {
    if (!open) return
    const currentSections = sectionsRef.current
    const params = new URLSearchParams(window.location.search)
    setSelectedPopularTags(urlParamsUtils.getTagsFromUrl('popular', currentSections))
    setSelectedExperienceTags(urlParamsUtils.getTagsFromUrl('experience', currentSections))
    setSelectedFacilityTags(urlParamsUtils.getTagsFromUrl('facility', currentSections))
    setSelectedAccommodationTags(urlParamsUtils.getTagsFromUrl('accommodation', currentSections))
    setSelectedBudgetTags(urlParamsUtils.getTagsFromUrl('budget', currentSections))
    setSelectedLifeTags(urlParamsUtils.getTagsFromUrl('life', currentSections))
    setSelectedPrivilegeTags(urlParamsUtils.getTagsFromUrl('privilege', currentSections))
    setSelectedTransportTags(urlParamsUtils.getTagsFromUrl('transport', currentSections))
    setSelectedChildTags(urlParamsUtils.getTagsFromUrl('child', currentSections))
    setSelectedAreaTags(urlParamsUtils.getTagsFromUrl('area', currentSections))
    setSearchText(params.get('text_search') || '')
  }, [open])

  // タグの選択/解除（共通化）
  const handleToggleTag = (keyValue: string, selected: string[], setSelected: React.Dispatch<React.SetStateAction<string[]>>) => {
    // 選択状態を反転
    const newSelected = selected.includes(keyValue) ? selected.filter((v) => v !== keyValue) : [...selected, keyValue]
    setSelected(newSelected)
  }

  // クリア
  const handleClear = () => {
    setSelectedPopularTags([])
    setSelectedExperienceTags([])
    setSelectedFacilityTags([])
    setSelectedAccommodationTags([])
    setSelectedBudgetTags([])
    setSelectedLifeTags([])
    setSelectedPrivilegeTags([])
    setSelectedTransportTags([])
    setSelectedChildTags([])
    setSelectedAreaTags([])
    setSearchText('')
  }

  // 適用
  const handleApply = () => {
    const currentQueryParams = urlParamsUtils.getCurrentParams()

    // 1. 各セクションの選択値をkeyごとに集約
    const tagsInOtherSections = new Set<string>()
    sections.forEach((section) => {
      if (section.id === 'popular') return
      section.tags.forEach((tag) => {
        tagsInOtherSections.add(`${tag.key}:${tag.value}`)
      })
    })
    const popularOnlySelected = selectedPopularTags.filter((keyValue) => !tagsInOtherSections.has(keyValue))

    const allSelected = [
      { sectionId: 'popular', selected: popularOnlySelected },
      { sectionId: 'experience', selected: selectedExperienceTags },
      { sectionId: 'facility', selected: selectedFacilityTags },
      { sectionId: 'accommodation', selected: selectedAccommodationTags },
      { sectionId: 'budget', selected: selectedBudgetTags },
      { sectionId: 'life', selected: selectedLifeTags },
      { sectionId: 'privilege', selected: selectedPrivilegeTags },
      { sectionId: 'transport', selected: selectedTransportTags },
      { sectionId: 'child', selected: selectedChildTags },
      { sectionId: 'area', selected: selectedAreaTags },
    ]

    // フィルターセクションのkeyを収集
    const filterKeys = new Set<string>()
    allSelected.forEach(({ sectionId }) => {
      const section = sections.find((s) => s.id === sectionId)
      if (!section) return
      section.tags.forEach((tag) => {
        filterKeys.add(tag.key)
      })
    })

    // text_searchもフィルターキーに追加
    filterKeys.add('text_search')

    // フィルター関連のパラメータをクリア
    urlParamsUtils.clearFilterParams(currentQueryParams, filterKeys)

    // タグをkeyごとにマージ
    const keyToValues = urlParamsUtils.mergeTagsByKey(allSelected, sections)

    // パラメータを更新
    const updates: Record<string, string | null> = {}
    keyToValues.forEach((set, key) => {
      if (set.size > 0) {
        updates[key] = Array.from(set).join(',')
      }
    })

    // text_searchも追加（空の場合は削除）
    if (searchText) {
      updates.text_search = searchText
    } else {
      updates.text_search = null
    }

    urlParamsUtils.updateParams(currentQueryParams, updates)
    urlParamsUtils.resetPageNumber(currentQueryParams)
    urlParamsUtils.navigateWithParams(currentQueryParams)
  }

  if (!open) return null

  return (
    <>
      <div
        className="modal-search-backdrop fixed top-0 left-0 right-0 z-50 w-full overflow-x-hidden overflow-y-auto md:inset-0 max-h-full h-full justify-center items-center flex"
        role="dialog"
        style={{ background: 'rgba(0,0,0,0.5)' }}
      >
        <div
          className="relative w-full overflow-y-auto remove-scroll-bar"
          style={{
            width: isMobileScreen ? '100%' : '420px',
            display: isMobileScreen ? 'block' : 'flex',
            alignItems: 'normal',
            height: isMobileScreen ? '100%' : isTabletScreen ? '77%' : '85%',
            animation: 'slide-down 0.4s ease-in-out forwards',
            borderRadius: isMobileScreen ? '0px' : '22px',
          }}
        >
          <div
            className="shadow-sm dark:bg-gray-700"
            style={{
              height: '100%',
              width: '100%',
              backgroundColor: isMobileScreen ? 'white' : 'transparent',
              borderRadius: isMobileScreen ? '0px' : '22px',
              marginLeft: isMobileScreen ? '0px' : 'auto',
              marginRight: isMobileScreen ? '0px' : 'auto',
            }}
          >
            <div
              className="flex bg-white items-center justify-between p-4 rounded-t"
              style={{
                borderRadius: isMobileScreen ? '0' : '16px 16px 0px 0px',
                height: isMobileScreen ? '60px' : '8%',
                minHeight: '60px',
              }}
            >
              <h3 className="mt-1 font-semibold text-black text-base">詳細検索</h3>
              <button
                className="text-gray-900 bg-transparent hover:bg-gray-200 rounded-lg text-sm ml-auto inline-flex items-center p-1.5"
                onClick={onClose}
                aria-label="閉じる"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path
                    clipRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    fillRule="evenodd"
                  />
                </svg>
              </button>
            </div>
            <div
              className="space-y-6 md:mb-0 bg-white"
              style={{
                height: isSmallMobileScreen
                  ? 'calc(100dvh - 120px)'
                  : isMobileScreen
                    ? 'calc(100dvh - 120px)'
                    : isTabletScreen
                      ? '75%'
                      : 'unset',
                maxHeight: isSmallMobileScreen ? 'calc(100dvh - 120px)' : '80%',
                overflow: 'auto',
                padding: isSmallMobileScreen ? '10px' : '1rem 1.5rem 0.5rem 1.5rem',
              }}
            >
              {sections.map((section, _idx) =>
                section.id === 'popular' ? null : (
                  <React.Fragment key={section.id}>
                    <div
                      ref={(el) => {
                        sectionRefs.current[section.id as FilterSectionKey] = el
                      }}
                      className="py-2"
                    >
                      <div className="mb-2 font-a1 font-size-14">{section.label}</div>
                      {section.id === 'experience' && (
                        <div className="flex flex-wrap gap-3">
                          {section.tags.map((tag) => {
                            const keyValue = `${tag.key}:${tag.value}`
                            const checked = selectedExperienceTags.includes(keyValue)
                            return (
                              <TagButton
                                key={`${section.id}-${tag.key}-${tag.value}`}
                                label={tag.label}
                                checked={checked}
                                onClick={() => handleToggleTag(keyValue, selectedExperienceTags, setSelectedExperienceTags)}
                              />
                            )
                          })}
                        </div>
                      )}
                      {section.id === 'facility' && (
                        <div className="flex flex-wrap gap-3">
                          {section.tags.map((tag) => {
                            const keyValue = `${tag.key}:${tag.value}`
                            const checked = selectedFacilityTags.includes(keyValue)
                            return (
                              <TagButton
                                key={`${section.id}-${tag.key}-${tag.value}`}
                                label={tag.label}
                                checked={checked}
                                onClick={() => handleToggleTag(keyValue, selectedFacilityTags, setSelectedFacilityTags)}
                              />
                            )
                          })}
                        </div>
                      )}
                      {section.id === 'accommodation' && (
                        <div className="flex flex-wrap gap-3">
                          {section.tags.map((tag) => {
                            const keyValue = `${tag.key}:${tag.value}`
                            const checked = selectedAccommodationTags.includes(keyValue)
                            return (
                              <TagButton
                                key={`${section.id}-${tag.key}-${tag.value}`}
                                label={tag.label}
                                checked={checked}
                                onClick={() => handleToggleTag(keyValue, selectedAccommodationTags, setSelectedAccommodationTags)}
                              />
                            )
                          })}
                        </div>
                      )}
                      {section.id === 'budget' && (
                        <div className="flex flex-wrap gap-3">
                          {section.tags.map((tag) => {
                            const keyValue = `${tag.key}:${tag.value}`
                            const checked = selectedBudgetTags.includes(keyValue)
                            return (
                              <TagButton
                                key={`${section.id}-${tag.key}-${tag.value}`}
                                label={tag.label}
                                checked={checked}
                                onClick={() => handleToggleTag(keyValue, selectedBudgetTags, setSelectedBudgetTags)}
                              />
                            )
                          })}
                        </div>
                      )}
                      {section.id === 'life' && (
                        <div className="flex flex-wrap gap-3">
                          {section.tags.map((tag) => {
                            const keyValue = `${tag.key}:${tag.value}`
                            const checked = selectedLifeTags.includes(keyValue)
                            return (
                              <TagButton
                                key={`${section.id}-${tag.key}-${tag.value}`}
                                label={tag.label}
                                checked={checked}
                                onClick={() => handleToggleTag(keyValue, selectedLifeTags, setSelectedLifeTags)}
                              />
                            )
                          })}
                        </div>
                      )}
                      {section.id === 'privilege' && (
                        <div className="flex flex-wrap gap-3">
                          {section.tags.map((tag) => {
                            const keyValue = `${tag.key}:${tag.value}`
                            const checked = selectedPrivilegeTags.includes(keyValue)
                            return (
                              <TagButton
                                key={`${section.id}-${tag.key}-${tag.value}`}
                                label={tag.label}
                                checked={checked}
                                onClick={() => handleToggleTag(keyValue, selectedPrivilegeTags, setSelectedPrivilegeTags)}
                              />
                            )
                          })}
                        </div>
                      )}
                      {section.id === 'transport' && (
                        <div className="flex flex-wrap gap-3">
                          {section.tags.map((tag) => {
                            const keyValue = `${tag.key}:${tag.value}`
                            const checked = selectedTransportTags.includes(keyValue)
                            return (
                              <TagButton
                                key={`${section.id}-${tag.key}-${tag.value}`}
                                label={tag.label}
                                checked={checked}
                                onClick={() => handleToggleTag(keyValue, selectedTransportTags, setSelectedTransportTags)}
                              />
                            )
                          })}
                        </div>
                      )}
                      {section.id === 'child' && (
                        <>
                          <div className="flex flex-wrap gap-3">
                            {section.tags.map((tag) => {
                              const keyValue = `${tag.key}:${tag.value}`
                              const checked = selectedChildTags.includes(keyValue)
                              return (
                                <TagButton
                                  key={`${section.id}-${tag.key}-${tag.value}`}
                                  label={tag.label}
                                  checked={checked}
                                  onClick={() => handleToggleTag(keyValue, selectedChildTags, setSelectedChildTags)}
                                />
                              )
                            })}
                          </div>
                        </>
                      )}
                      {section.id === 'area' && (
                        <div className="flex flex-wrap gap-3">
                          {section.tags.map((tag) => {
                            const keyValue = `${tag.key}:${tag.value}`
                            const checked = selectedAreaTags.includes(keyValue)
                            return (
                              <TagButton
                                key={`${section.id}-${tag.key}-${tag.value}`}
                                label={tag.label}
                                checked={checked}
                                onClick={() => handleToggleTag(keyValue, selectedAreaTags, setSelectedAreaTags)}
                              />
                            )
                          })}
                        </div>
                      )}
                      {section.id === 'text_search' && (
                        <div className="py-2">
                          <div className="relative w-full">
                            <SuggestionTextSearch textSearch={searchText} onChange={handleSearchChange} />
                          </div>
                        </div>
                      )}
                    </div>
                  </React.Fragment>
                ),
              )}
            </div>
            <div
              className="flex w-full items-center p-4 space-x-2 rounded-b bg-white"
              style={{
                borderRadius: isMobileScreen ? '0px' : '0px 0px 16px 16px',
                position: isMobileScreen ? 'fixed' : 'unset',
                bottom: isMobileScreen ? '0' : 'unset',
                left: isMobileScreen ? '0' : 'unset',
                right: isMobileScreen ? '0' : 'unset',
                height: isMobileScreen || isTabletScreen ? '60px' : '10%',
                zIndex: 10,
              }}
            >
              <button
                className="w-1/2 md:w-1/2 text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-hidden focus:ring-blue-300 rounded-lg border border-gray-200 text-sm font-medium px-5 hover:text-gray-900 focus:z-10 py-2.5"
                style={{
                  padding: isSmallMobileScreen ? '0.4rem 1rem' : '0.7rem 1rem',
                }}
                type="button"
                onClick={handleClear}
              >
                クリア
              </button>
              <button
                className="w-1/2 md:w-1/2 background-green-primary text-white focus:ring-4 focus:outline-hidden focus:ring-blue-300 font-medium rounded-lg text-sm px-5 text-center py-2.5"
                style={{
                  padding: isSmallMobileScreen ? '0.4rem 1rem' : '0.7rem 1rem',
                }}
                type="button"
                onClick={handleApply}
              >
                適用
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
